import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Pause, Play, X, CreditCard, Repeat, Calendar as CalIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

const INTERVAL_LABELS = { weekly: "Weekly", biweekly: "Every 2 weeks", monthly: "Monthly" };
const STATUS_STYLES = {
  active: "bg-secondary/10 text-secondary border border-secondary/20",
  pending_payment: "bg-amber-50 text-amber-700 border border-amber-200",
  paused: "bg-slate-100 text-slate-700 border border-slate-300",
  cancelled: "bg-red-50 text-red-700 border border-red-200",
};

export default function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await http.get("/subscriptions");
      setSubs(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading subscriptions…</div>;

  if (subs.length === 0) {
    return (
      <Card className="border-dashed" data-testid="no-subscriptions">
        <CardContent className="p-10 text-center">
          <Sparkles size={22} className="mx-auto text-secondary mb-2" />
          <div className="font-display font-semibold text-primary">No recurring plans yet</div>
          <p className="text-sm text-muted-foreground mt-1">Set up a recurring cleaning and never think about it again.</p>
          <Button asChild className="mt-4 bg-primary hover:bg-primary/90" data-testid="cta-first-subscription">
            <a href="/booking?mode=subscription">Start a plan</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="subscriptions-tab">
      {subs.map((s) => <SubscriptionCard key={s.id} sub={s} onChange={load} />)}
    </div>
  );
}

function SubscriptionCard({ sub, onChange }) {
  const nextDate = (sub.next_visit_date || sub.next_charge_date || "").slice(0, 10);
  return (
    <Card className="border-border/60" data-testid={`sub-card-${sub.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display font-semibold text-primary text-lg">{sub.service_name}</div>
              <Badge className={STATUS_STYLES[sub.status] || "bg-muted"}>{sub.status.replace("_", " ")}</Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-1">{INTERVAL_LABELS[sub.interval] || sub.interval} · ${sub.price?.toFixed(2)}/cycle</div>
            {sub.status === "active" && nextDate && (
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CalIcon size={11} /> Next visit: <b className="text-primary">{nextDate}</b></div>
            )}
            {sub.status === "paused" && sub.paused_until && (
              <div className="text-xs text-muted-foreground mt-1">Paused until {sub.paused_until.slice(0, 10)}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {sub.status === "active" && <ChangeFrequency sub={sub} onChange={onChange} />}
            {sub.status === "active" && <PauseAction sub={sub} onChange={onChange} />}
            {sub.status === "paused" && <ResumeAction sub={sub} onChange={onChange} />}
            {(sub.status === "active" || sub.status === "paused") && <CancelAction sub={sub} onChange={onChange} />}
            {sub.status === "pending_payment" && <DiscardPendingAction sub={sub} onChange={onChange} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangeFrequency({ sub, onChange }) {
  const [open, setOpen] = useState(false);
  const [interval, setInterval] = useState(sub.interval);

  async function save() {
    try {
      await http.post(`/subscriptions/${sub.id}/change-frequency`, { interval });
      toast.success("Frequency updated");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`sub-change-freq-${sub.id}`}><Repeat size={13} className="mr-1" /> Change frequency</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Change cleaning frequency</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Choose how often you want cleanings. Change takes effect at the next cycle.</p>
          <Select value={interval} onValueChange={setInterval}>
            <SelectTrigger data-testid={`sub-freq-select-${sub.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly — $129/visit</SelectItem>
              <SelectItem value="biweekly">Every 2 weeks — $89/visit</SelectItem>
              <SelectItem value="monthly">Monthly — $59/visit</SelectItem>
            </SelectContent>
          </Select>
          <Button className="w-full bg-primary hover:bg-primary/90" onClick={save} data-testid={`sub-freq-save-${sub.id}`}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PauseAction({ sub, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function pause() {
    setBusy(true);
    try {
      await http.post(`/subscriptions/${sub.id}/pause`, { until: null });
      toast.success("Plan paused");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`sub-pause-${sub.id}`}><Pause size={13} className="mr-1" /> Pause</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Pause cleanings?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">We'll stop generating new visits and charges until you press Resume. Any scheduled visits stay as-is.</p>
        <Button className="mt-3 w-full bg-primary hover:bg-primary/90" disabled={busy} onClick={pause} data-testid={`sub-pause-confirm-${sub.id}`}>{busy ? "Pausing…" : "Yes, pause"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function ResumeAction({ sub, onChange }) {
  const [busy, setBusy] = useState(false);

  async function resume() {
    setBusy(true);
    try {
      await http.post(`/subscriptions/${sub.id}/resume`);
      toast.success("Plan resumed");
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return <Button size="sm" className="bg-secondary hover:bg-secondary/90" disabled={busy} onClick={resume} data-testid={`sub-resume-${sub.id}`}><Play size={13} className="mr-1" /> Resume</Button>;
}

function CancelAction({ sub, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function cancel() {
    setBusy(true);
    try {
      await http.post(`/subscriptions/${sub.id}/cancel`);
      toast.success("Subscription cancelled");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`sub-cancel-${sub.id}`}><X size={13} className="mr-1" /> Cancel</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Cancel plan?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">All future visits will be cancelled, and no more charges will go through. You can start a new plan any time.</p>
        <Button className="mt-3 w-full bg-red-600 hover:bg-red-700" disabled={busy} onClick={cancel} data-testid={`sub-cancel-confirm-${sub.id}`}>{busy ? "Cancelling…" : "Yes, cancel plan"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function DiscardPendingAction({ sub, onChange }) {
  const [busy, setBusy] = useState(false);

  async function discard() {
    if (!window.confirm("Discard this pending plan? You can start a new one any time.")) return;
    setBusy(true);
    try {
      await http.delete(`/subscriptions/${sub.id}`);
      toast.success("Discarded");
      onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <Button size="sm" variant="outline" asChild data-testid={`sub-resume-checkout-${sub.id}`}>
        <a href="/booking?mode=subscription"><CreditCard size={13} className="mr-1" /> Resume payment</a>
      </Button>
      <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50"
              disabled={busy} onClick={discard} data-testid={`sub-discard-${sub.id}`}>
        <X size={13} className="mr-1" /> Discard
      </Button>
    </>
  );
}
